import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { InputDirective } from '../ui/input/input.directive';
import { SelectComponent } from '../ui/select/select.component';
import { ButtonComponent } from '../ui/button/button.component';
import type { SelectOption } from '../ui/select/select.component';

export interface FilingFilter {
  search: string;
  owner: string;
  team: string;
  status: string;
}

@Component({
  selector: 'app-search-filter',
  imports: [InputDirective, SelectComponent, ButtonComponent],
  template: `
    <div class="flex flex-wrap gap-2 items-center">
      <input
        appInput
        type="search"
        [placeholder]="placeholder()"
        [value]="search()"
        (input)="search.set($any($event.target).value)"
        class="w-48"
        aria-label="Search"
      />
      <app-select
        [options]="ownerOptions()"
        placeholder="All owners"
        [value]="owner()"
        (valueChange)="owner.set($event)"
      />
      @if (teams().length > 0) {
        <app-select
          [options]="teamOptions()"
          placeholder="All teams"
          [value]="team()"
          (valueChange)="team.set($event)"
        />
      }
      <app-select
        [options]="statusOptions()"
        placeholder="All statuses"
        [value]="status()"
        (valueChange)="status.set($event)"
      />
      @if (hasFilters()) {
        <button appButton variant="ghost" size="sm" (click)="clearFilters()">
          Clear
        </button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFilterComponent {
  owners = input<string[]>([]);
  teams = input<string[]>([]);
  statuses = input<string[]>(['all', 'pending', 'completed', 'overdue']);
  placeholder = input('Search filings...');
  filterChange = output<FilingFilter>();

  protected readonly search = signal('');
  protected readonly owner = signal('');
  protected readonly team = signal('');
  protected readonly status = signal('');

  protected readonly hasFilters = computed(
    () => !!(this.search() || this.owner() || this.team() || this.status())
  );

  protected readonly ownerOptions = computed<SelectOption[]>(() =>
    this.owners().map(o => ({ value: o, label: o }))
  );

  protected readonly teamOptions = computed<SelectOption[]>(() =>
    this.teams().map(t => ({ value: t, label: t }))
  );

  protected readonly statusOptions = computed<SelectOption[]>(() =>
    this.statuses()
      .filter(s => s !== 'all')
      .map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))
  );

  readonly #filter = computed<FilingFilter>(() => ({
    search: this.search(),
    owner: this.owner(),
    team: this.team(),
    status: this.status(),
  }));

  constructor() {
    effect(() => this.filterChange.emit(this.#filter()));
  }

  protected clearFilters(): void {
    this.search.set('');
    this.owner.set('');
    this.team.set('');
    this.status.set('');
  }
}
